import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { RunLogEntry } from '../types';
import { authFetch } from '../api';

interface LogsPanelProps {
  workflowId?: string;
}

interface LogsResponse {
  count: number;
  total: number;
  logs: RunLogEntry[];
}

function formatContextValue(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export default function LogsPanel({ workflowId }: LogsPanelProps) {
  const [logs, setLogs] = useState<RunLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [onlyCurrentWorkflow, setOnlyCurrentWorkflow] = useState(false);
  const [replayingRunId, setReplayingRunId] = useState('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '100');
    if (onlyCurrentWorkflow && workflowId) {
      params.set('workflow_id', workflowId);
    }
    return params.toString();
  }, [onlyCurrentWorkflow, workflowId]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`/api/logs?${queryString}`);
      if (!res.ok) throw new Error(`Failed to load logs (${res.status})`);
      const payload: LogsResponse = await res.json();
      setLogs(Array.isArray(payload.logs) ? payload.logs : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load logs');
    }
    setLoading(false);
  }, [queryString]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const clearLogs = async () => {
    if (!window.confirm('Delete all run logs?')) return;
    setLoading(true);
    setError('');
    try {
      const res = await authFetch('/api/logs', { method: 'DELETE' });
      if (!res.ok) throw new Error(`Failed to clear logs (${res.status})`);
      setLogs([]);
      setExpandedRunId(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to clear logs');
    }
    setLoading(false);
  };

  const replayRun = async (runId: string) => {
    if (!runId || replayingRunId) return;
    setReplayingRunId(runId);
    setError('');
    try {
      const res = await authFetch(`/api/logs/${runId}/replay`, { method: 'POST' });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(String(payload?.detail || `Replay failed (${res.status})`));
      }
      await loadLogs();
    } catch (err: any) {
      setError(err?.message || 'Replay failed');
    }
    setReplayingRunId('');
  };

  return (
    <div className="logs-panel">
      <div className="logs-header">
        <h3>Run Logs</h3>
        <div className="logs-actions">
          <button className="btn-secondary" onClick={loadLogs} disabled={loading}>
            Refresh
          </button>
          <button className="btn-secondary" onClick={clearLogs} disabled={loading || logs.length === 0}>
            Clear
          </button>
        </div>
      </div>

      <div className="logs-filters">
        <label>
          <input
            type="checkbox"
            checked={onlyCurrentWorkflow}
            onChange={e => setOnlyCurrentWorkflow(e.target.checked)}
            disabled={!workflowId}
          />
          Only current workflow
        </label>
      </div>

      {error && <div className="settings-status error">{error}</div>}

      <div className="logs-list">
        {!loading && logs.length === 0 && <div className="logs-empty">No runs yet.</div>}

        {logs.map(log => {
          const isExpanded = expandedRunId === log.run_id;
          const time = new Date(log.started_at).toLocaleString();
          return (
            <div key={log.run_id} className="log-item">
              <div className="log-item-top">
                <div className="log-item-query">{log.query}</div>
                <span className={`log-status ${log.status}`}>{log.status}</span>
              </div>

              <div className="log-meta">
                <span>{time}</span>
                <span>{log.workflow_id}</span>
                <span>{log.duration_ms} ms</span>
                <span>{log.source.toUpperCase()}</span>
                {typeof log?.run_metrics?.total_tokens === 'number' && (
                  <span>{log.run_metrics.total_tokens} tokens</span>
                )}
                {typeof log?.run_metrics?.estimated_cost_usd === 'number' && (
                  <span>${Number(log.run_metrics.estimated_cost_usd).toFixed(6)}</span>
                )}
              </div>

              <div className="log-answer-preview">{log.final_answer || '(empty answer)'}</div>

              <button className="trace-toggle" onClick={() => setExpandedRunId(isExpanded ? null : log.run_id)}>
                {isExpanded ? 'Hide' : 'Show'} node trace ({log.node_count} nodes)
              </button>
              <button
                className="trace-toggle"
                onClick={() => replayRun(log.run_id)}
                disabled={!!replayingRunId}
              >
                {replayingRunId === log.run_id ? 'Replaying...' : 'Replay run'}
              </button>

              {isExpanded && (
                <div className="trace-list">
                  {log.results.map(nr => (
                    <div key={`${log.run_id}-${nr.node_id}`} className="trace-item">
                      <div className="trace-item-header">
                        <div className={`status-dot ${nr.status}`} />
                        <span className="trace-item-label">{nr.label}</span>
                        <span className="trace-item-type">{nr.node_type}</span>
                      </div>
                      {nr.input_context && Object.keys(nr.input_context).length > 0 && (
                        <div className="trace-item-context">
                          {Object.entries(nr.input_context).map(([key, value]) => (
                            <div key={key} className="trace-item-context-row">
                              <span className="trace-item-context-key">{key}</span>
                              <span>{formatContextValue(value)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="trace-item-output">{nr.output}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
